<template>
  <v-container>
    <div class="d-flex align-center">
      <h2>Daily Journal Summary</h2>
      <v-spacer></v-spacer>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            color="primary"
            class="mr-2"
            outlined
            v-bind="attrs"
            v-on="on"
            :disabled="!journal_entries.length"
          >
            <v-icon left small>mdi-download</v-icon>Export
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="exportJSON">
            <v-list-item-icon class="mr-2"
              ><v-icon small>mdi-code-json</v-icon></v-list-item-icon
            >
            <v-list-item-title>Export as JSON</v-list-item-title>
          </v-list-item>
          <v-list-item @click="exportPDF">
            <v-list-item-icon class="mr-2"
              ><v-icon small>mdi-file-pdf-box</v-icon></v-list-item-icon
            >
            <v-list-item-title>Export as PDF</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn small color="success" @click="refresh">Refresh</v-btn>
    </div>

    <v-data-table
      v-if="!loading"
      :items="journal_entries"
      :headers="journal_headers"
      :loading="loading"
    >
      <!-- for debit and credit entries -->
      <template v-slot:item.credit="{ item }">
        <span v-if="!item.is_debit">{{ item.amount }}</span>
      </template>
      <template v-slot:item.debit="{ item }">
        <span v-if="item.is_debit">{{ item.amount }}</span>
      </template>

      <!-- colors of account title -->
      <template v-slot:item.account_title="{ item }">
        <v-chip
          color="success"
          v-if="item.chart_of_account.account_title == 'Cash'"
          >{{ item.chart_of_account.account_title }}</v-chip
        >

        <v-chip
          color="orange"
          v-else-if="
            item.chart_of_account.account_title == 'Accounts Receivables'
          "
          >{{ item.chart_of_account.account_title }}</v-chip
        >
        <v-chip
          color="yellow"
          v-else-if="item.chart_of_account.account_title == 'Service Revenue'"
          >{{ item.chart_of_account.account_title }}</v-chip
        >

        <v-chip
          color="primary"
          v-else-if="item.chart_of_account.account_title == 'Sales/Item Sales'"
          >{{ item.chart_of_account.account_title }}</v-chip
        >

        <v-chip
          color="primary"
          v-else-if="item.chart_of_account.account_title == 'Gcash'"
          >{{ item.chart_of_account.account_title }}</v-chip
        >

        <v-chip
          color="error"
          v-else-if="item.chart_of_account.account_title == 'Software Revenues'"
          >{{ item.chart_of_account.account_title }}</v-chip
        >

        <v-chip color="grey" v-else>
          {{ item.chart_of_account.account_title }}
        </v-chip>
      </template>
      <!-- for invoice id -->
      <template v-slot:item.invoice_id="{ item }">
        <v-chip :color="getColor(item.invoice_id)">{{
          item.invoice_id
        }}</v-chip>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn small icon color="red" @click="del(item)" :loading="loading"
          ><v-icon>mdi-delete</v-icon></v-btn
        >
      </template>
    </v-data-table>

    <h2>
      Total Debit:
      <span class="success--text">{{ total_debit_credit.debit }}</span> | Total
      Credit:
      <span class="primary--text"> {{ total_debit_credit.credit }}</span>
    </h2>
    <v-btn class="mt-4" color="primary" @click="post_journal" :loading="loading"
      >Post Journal</v-btn
    >
  </v-container>
</template>

<script>
// import { mapActions } from "vuex";
import axios from "axios";
export default {
  data() {
    return {
      invoice_id_colors_collection: {},
      loading: false,
      poll_timer: null,
      journal_entries: [],
      journal_headers: [
        { text: "Action", value: "action" },
        { text: "Journal ID", value: "id" },
        { text: "Account Title", value: "account_title" },
        { text: "Debit", value: "debit" },
        { text: "Credit", value: "credit" },
        { text: "Date", value: "created_at" },
        { text: "Remarks", value: "remarks" },
        { text: "Invoice ID", value: "invoice_id" },
        { text: "Is equal", value: "is_equal" },
      ],
    };
  },
  created() {
    this.get_daily_journals();
    this.ready = true;
    // Auto-refresh so new journal entries from transactions (payments,
    // invoices, etc.) appear without manually clicking Refresh.
    this.poll_timer = setInterval(() => {
      this.get_daily_journals(true);
    }, 15000);
  },
  beforeDestroy() {
    if (this.poll_timer) clearInterval(this.poll_timer);
  },
  computed: {
    total_debit_credit() {
      return this.journal_entries.reduce(
        (totals, entry) => {
          if (entry.is_debit) {
            totals.debit += Math.round(parseFloat(entry.amount));
          } else {
            totals.credit += Math.round(parseFloat(entry.amount));
          }
          return totals;
        },
        { credit: 0, debit: 0 }
      );
    },
  },
  methods: {
    getColor(targetId) {
      console.log("triggered");
      const foundObject = this.invoice_id_colors_collection.find(
        (obj) => obj.id === targetId
      );
      return foundObject.color;
    },
    async get_daily_journals(silent = false) {
      // Background polling refreshes silently so the table doesn't flicker
      // (the table is hidden while `loading` is true).
      if (!silent) this.loading = true;
      try {
        let response = await axios.get("/get_daily_journals");
        this.journal_entries = response.data;
        this.get_collection_of_invoice_id();
      } catch (e) {
        console.warn(
          "[JournalDailySummary] Failed to fetch daily journals:",
          e.message
        );
      }
      if (!silent) this.loading = false;
    },

    combineArraysToObject(a) {
      // Initialize an empty array to store the result
      let result = [];
      // Iterate through array 'a' and create objects
      for (let i = 0; i < a.length; i++) {
        let color = i % 2 === 0 ? "primary" : "error";
        result.push({ id: a[i], color: color });
      }

      return result;
    },

    get_collection_of_invoice_id() {
      if (this.journal_entries.length === 0) {
        this.invoice_id_colors_collection = [];
        return;
      }

      var collected_id = [];

      // get the first id
      let last_index = this.journal_entries.length - 1;
      let first_id = this.journal_entries[last_index]["invoice_id"];

      collected_id.push(first_id);

      this.journal_entries.forEach((j) => {
        if (!collected_id.includes(j.invoice_id))
          collected_id.push(j.invoice_id);
      });
      let x = this.combineArraysToObject(collected_id);
      this.invoice_id_colors_collection = x;
    },
    refresh() {
      this.get_daily_journals();
    },
    // Maps a journal entry into a flat, export-friendly row.
    mapEntryForExport(item) {
      return {
        journal_id: item.id,
        account_title: item.chart_of_account
          ? item.chart_of_account.account_title
          : "",
        debit: item.is_debit ? Number(item.amount) : 0,
        credit: item.is_debit ? 0 : Number(item.amount),
        date: item.created_at,
        remarks: item.remarks,
        invoice_id: item.invoice_id,
        is_equal: item.is_equal,
      };
    },
    todayStamp() {
      // Local date as YYYY-MM-DD (matches Deposit Records / Invoices exports).
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    exportJSON() {
      if (!this.journal_entries.length) return;
      try {
        const payload = {
          exported_at: new Date().toISOString(),
          total_debit: this.total_debit_credit.debit,
          total_credit: this.total_debit_credit.credit,
          count: this.journal_entries.length,
          entries: this.journal_entries.map((e) => this.mapEntryForExport(e)),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `daily-journal-summary-${this.todayStamp()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.$alert({
          title: "Success",
          message: "Daily journal summary exported as JSON.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: "Failed to export JSON: " + e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    exportPDF() {
      if (!this.journal_entries.length) return;
      const esc = (v) =>
        String(v === null || v === undefined ? "" : v).replace(
          /[&<>]/g,
          (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])
        );
      const fmt = (n) =>
        Number(n).toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const rows = this.journal_entries
        .map((item) => {
          const r = this.mapEntryForExport(item);
          return `<tr>
            <td>${esc(r.journal_id)}</td>
            <td>${esc(r.account_title)}</td>
            <td class="num">${r.debit ? fmt(r.debit) : ""}</td>
            <td class="num">${r.credit ? fmt(r.credit) : ""}</td>
            <td>${esc(r.date)}</td>
            <td>${esc(r.remarks)}</td>
            <td>${esc(r.invoice_id)}</td>
          </tr>`;
        })
        .join("");
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
        <title>Daily Journal Summary</title>
        <style>
          @media print { @page { margin: 0.4in; } }
          * { box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; color: #2c3e50; padding: 16px; font-size: 11px; }
          .header { border-bottom: 3px double #1976d2; padding-bottom: 6px; margin-bottom: 10px; }
          .company-name { font-size: 20px; font-weight: bold; letter-spacing: 1px; }
          .company-name .red { color: #d32f2f; } .company-name .blue { color: #1976d2; }
          .doc-title { font-size: 15px; font-weight: 700; color: #1976d2; margin-top: 4px; }
          .meta { font-size: 10px; color: #555; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
          th { background: #1976d2; color: #fff; font-size: 10px; }
          tr:nth-child(even) td { background: #f5f7fa; }
          td.num, th.num { text-align: right; }
          tfoot td { font-weight: bold; background: #eef3fb; }
        </style></head>
        <body>
          <div class="header">
            <div class="company-name"><span class="red">JMB</span> <span class="blue">Computers</span></div>
            <div class="doc-title">Daily Journal Summary</div>
            <div class="meta">Generated: ${esc(
              new Date().toLocaleString()
            )}</div>
          </div>
          <table>
            <thead><tr>
              <th>Journal ID</th><th>Account Title</th>
              <th class="num">Debit</th><th class="num">Credit</th>
              <th>Date</th><th>Remarks</th><th>Invoice ID</th>
            </tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr>
              <td colspan="2">Totals</td>
              <td class="num">${fmt(this.total_debit_credit.debit)}</td>
              <td class="num">${fmt(this.total_debit_credit.credit)}</td>
              <td colspan="3"></td>
            </tr></tfoot>
          </table>
        </body></html>`;
      const printWindow = window.open("", "", "width=1100,height=700");
      if (!printWindow) {
        this.$alert({
          title: "Error",
          message: "Popup blocked. Please allow popups to export the PDF.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    },
    async del(item) {
      this.loading = true;
      let x = await this.$confirm({
        title: "Delete Entry",
        message: "Are you sure you want to proceed?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        try {
          await axios.delete("/journal_entry/" + item.id);
          let filtered = this.journal_entries.filter((c) => {
            return c.id != item.id;
          });
          this.$alert({
            title: "Success",
            message: "Successfully deleted an item",
            color: "success",
            icon: "mdi-check-circle",
          });
          this.journal_entries = filtered;
        } catch (e) {
          this.$alert({
            title: "Error",
            message: "Failed to delete: " + e.message,
            color: "error",
            icon: "mdi-alert-circle",
          });
        }
      }
      this.loading = false;
    },
    async post_journal() {
      if (
        Math.round(this.total_debit_credit.debit) !=
        Math.round(this.total_debit_credit.credit)
      ) {
        this.$alert({
          title: "Error",
          message: "ERROR: Debit and Credit is not equal. Please investigate",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      this.loading = true;
      let x = await this.$confirm({
        title: "Post Journal",
        message: "Are you sure you want to proceed?",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      if (x) {
        try {
          let journal_id = [];
          this.journal_entries.forEach((j) => {
            journal_id.push(j.id);
          });
          let request = { journal_id: journal_id };
          await axios.post("/post_daily_journal_entry", request);
          let response = await axios.get("/get_daily_journals");
          this.journal_entries = response.data;
          this.$alert({
            title: "Success",
            message: "Successfully posted daily journals",
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (e) {
          this.$alert({
            title: "Error",
            message: "Failed to post journal: " + e.message,
            color: "error",
            icon: "mdi-alert-circle",
          });
        }
      }
      this.loading = false;
    },
  },
};
</script>

<style></style>
